/**
 * M32107 Resignation of Secretary — Register 용 secretaries[] 빌드/해석.
 * TASK_DATA.formData.secretaries 또는 presetForm.values(sec{n}_*) 에서 추출.
 */

export type ResignRegisterSecretary = {
  fullName: string;
  nricOrPassport: string;
  residentialAddress: string;
  dateOfCessation: string;
  appointmentDate: string;
  contactCd: string;
  officerIdx: number | null;
  reasonForCessation: string;
  licenseType: string;
  licenseNumber: string;
  ssmPcNo: string;
};

function trim(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function toOfficerIdx(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Studio values + rows → Register Detail / L_OFFICER 갱신용 secretaries[] */
export function buildResignRegisterSecretaries(
  values: Record<string, any>,
  rows: number[],
  reasons?: Record<string | number, string>,
): ResignRegisterSecretary[] {
  const out: ResignRegisterSecretary[] = [];
  for (const id of rows) {
    const fullName = trim(values[`sec${id}_full_name`]);
    if (!fullName) continue;
    const reason =
      trim(reasons?.[id]) ||
      trim(reasons?.[String(id)]) ||
      trim(values[`sec${id}_reason`]) ||
      trim(values[`sec${id}_reason_for_cessation`]);
    out.push({
      fullName,
      nricOrPassport: trim(values[`sec${id}_ic_passport_no`]),
      residentialAddress: trim(values[`sec${id}_residential_address`]),
      dateOfCessation: trim(values[`sec${id}_date_of_cessation`]),
      appointmentDate: trim(values[`sec${id}_date_of_appointment`] || values[`sec${id}_appointment_date`]),
      contactCd: trim(values[`sec${id}_contact_cd`]),
      officerIdx: toOfficerIdx(values[`sec${id}_officer_idx`]),
      reasonForCessation: reason,
      licenseType: trim(values[`sec${id}_license_type`]),
      licenseNumber: trim(values[`sec${id}_license_number`]),
      ssmPcNo: trim(values[`sec${id}_ssm_pc_no`]),
    });
  }
  return out;
}

/** TASK_DATA.formData 에서 사임 비서 목록 해석 (legacy presetForm-only 포함) */
export function resolveResignRegisterSecretaries(
  formData: Record<string, any> | null | undefined,
): ResignRegisterSecretary[] {
  const fd = formData && typeof formData === 'object' ? formData : {};
  const fromRoot = Array.isArray(fd.secretaries) ? fd.secretaries : [];
  if (fromRoot.length > 0) {
    return fromRoot
      .map((row: any) => ({
        fullName: trim(row?.fullName || row?.name),
        nricOrPassport: trim(row?.nricOrPassport || row?.icPassportNo),
        residentialAddress: trim(row?.residentialAddress),
        dateOfCessation: trim(
          row?.dateOfCessation || row?.cessationDate || row?.date_of_cessation,
        ),
        appointmentDate: trim(row?.appointmentDate || row?.dateOfAppointment),
        contactCd: trim(row?.contactCd || row?.contact_cd),
        officerIdx: toOfficerIdx(row?.officerIdx || row?.officer_idx),
        reasonForCessation: trim(row?.reasonForCessation || row?.reason),
        licenseType: trim(row?.licenseType || row?.license_type),
        licenseNumber: trim(row?.licenseNumber || row?.license_number || row?.secretaryLicenseNo),
        ssmPcNo: trim(row?.ssmPcNo || row?.ssm_pc_no),
      }))
      .filter((d) => d.fullName);
  }

  const preset = fd.presetForm && typeof fd.presetForm === 'object' ? fd.presetForm : null;
  const values = (preset?.values && typeof preset.values === 'object' ? preset.values : null)
    || (fd.values && typeof fd.values === 'object' ? fd.values : null)
    || {};
  const local = preset?.local && typeof preset.local === 'object' ? preset.local : {};
  const rowsRaw = Array.isArray(local.rows) && local.rows.length ? local.rows : [1];
  const rows = rowsRaw.map((r: any) => Number(r)).filter((n: number) => Number.isFinite(n) && n > 0);
  return buildResignRegisterSecretaries(values, rows.length ? rows : [1], local.reasons || {});
}
